import React, { useMemo, useState } from 'react';
import type { AppState, RolPermiso } from '../types';
import { PageShell, glassLight, primaryButton, ghostButton, dangerButton, Badge, EmptyState, useIsMobile, usePagination, Pagination, T, Modal } from '../ui';
// Colores fijos para los primeros roles; el resto usa un color genérico
const ROL_COLORS = [{
  color: '#8b5cf6',
  soft: 'rgba(139,92,246,0.14)'
}, {
  color: T.info,
  soft: T.infoSoft
}, {
  color: T.accent,
  soft: T.accentSoft
}, {
  color: '#ec4899',
  soft: 'rgba(236,72,153,0.14)'
}, {
  color: '#10b981',
  soft: 'rgba(16,185,129,0.14)'
}];
interface Props {
  state: AppState;
  onNew: () => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  canArchive: boolean;
}
export function Usuarios({
  state,
  onNew,
  onEdit,
  onDelete,
  canArchive
}: Props) {
  const [tab, setTab] = useState<'activos' | 'archivados'>('activos');
  const [confirm, setConfirm] = useState<string | null>(null);
  const isMobile = useIsMobile();
  // Construir etiquetas y colores dinámicamente desde los roles reales
  const rolLabel: Record<string, string> = {};
  const rolColor: Record<string, {
    color: string;
    soft: string;
  }> = {};
  state.roles.forEach((r, i) => {
    const key = r.id.replace(/^rol-/, '');
    rolLabel[key] = r.nombre;
    rolColor[key] = ROL_COLORS[i % ROL_COLORS.length];
  });
  const filteredUsuarios = useMemo(() => {
    return state.usuarios.filter(u => tab === 'activos' ? !u.archivado : u.archivado);
  }, [state.usuarios, tab]);
  const {
    page,
    setPage,
    totalPages,
    pageItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(filteredUsuarios, 8);
  const sucNamesFor = (ids?: string[]) => {
    const safeIds = Array.isArray(ids) ? ids : [];
    return safeIds.length === 0 ? <span style={{
      color: '#c4b5fd',
      fontStyle: 'italic'
    }} data-id="element-862">
        Todas las sucursales
      </span> : safeIds.map(sid => state.sucs.find(x => x.id === sid)?.nombre ?? sid).join(', ');
  };
  return <PageShell title="Usuarios y acceso" sub="Gestiona quién puede entrar y qué sucursales puede ver" action={<button onClick={onNew} style={primaryButton} data-id="element-864">
          + Nuevo usuario
        </button>} data-id="element-863">
      <div style={{
      display: 'flex',
      gap: 8,
      marginBottom: 20
    }} data-id="element-865">
        <button onClick={() => {
        setTab('activos');
        setPage(1);
      }} style={{
        ...ghostButton,
        background: tab === 'activos' ? T.accentSoft : 'transparent',
        color: tab === 'activos' ? T.accent : T.textMuted
      }} data-id="element-866">
          Activos
        </button>
        {canArchive && <button onClick={() => {
        setTab('archivados');
        setPage(1);
      }} style={{
        ...ghostButton,
        background: tab === 'archivados' ? T.accentSoft : 'transparent',
        color: tab === 'archivados' ? T.accent : T.textMuted
      }} data-id="element-867">
            Archivados
          </button>}
      </div>

      {filteredUsuarios.length === 0 ? <EmptyState title="Sin usuarios registrados" hint="Agrega usuarios y define a qué sucursales tienen acceso." icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" data-id="element-869">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" data-id="element-870" />
              <circle cx="9" cy="7" r="4" data-id="element-871" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" data-id="element-872" />
            </svg>} data-id="element-868" /> : isMobile ? <>
          <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12
      }} data-id="element-873">
            {pageItems.map(u => {
          const rc = rolColor[u.rol] ?? {
            color: T.textMuted,
            soft: 'rgba(255,255,255,0.06)'
          };
          return <div key={u.id} style={{
            ...glassLight,
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 12
          }} data-id="element-874">
                  <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 10
            }} data-id="element-875">
                    <div style={{
                minWidth: 0
              }} data-id="element-876">
                      <div style={{
                  fontWeight: 700,
                  color: T.text,
                  fontSize: 15
                }} data-id="element-877">
                        {u.nombre}
                      </div>
                      <div style={{
                  fontSize: 13,
                  color: T.textSecondary,
                  marginTop: 2,
                  wordBreak: 'break-all'
                }} data-id="element-878">
                        {u.correo}
                      </div>
                    </div>
                    <div style={{
                flexShrink: 0
              }} data-id="element-879">
                      <Badge label={rolLabel[u.rol]} color={rc.color} soft={rc.soft} data-id="element-880" />
                    </div>
                  </div>
                  <div data-id="element-881">
                    <div style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '.1em',
                textTransform: 'uppercase',
                color: T.textMuted,
                marginBottom: 4
              }} data-id="element-882">
                      Sucursales asignadas
                    </div>
                    <div style={{
                fontSize: 13,
                color: T.textSecondary,
                lineHeight: 1.45
              }} data-id="element-883">
                      {sucNamesFor(u.sucursalesIds)}
                    </div>
                  </div>
                  <div style={{
              display: 'flex',
              gap: 8,
              paddingTop: 4,
              borderTop: `1px solid ${T.border}`
            }} data-id="element-884">
                    <button className="getr-btn-ghost" onClick={() => onEdit(u.id)} style={{
                ...ghostButton,
                flex: 1,
                marginTop: 8
              }} data-id="element-885">
                      Editar
                    </button>
                    {u.rol !== 'admin' && <button onClick={() => setConfirm(u.id)} style={{
                ...ghostButton,
                flex: 1,
                marginTop: 8,
                color: u.archivado ? T.info : T.warn,
                borderColor: u.archivado ? 'rgba(96,165,250,0.3)' : 'rgba(251,191,36,0.3)',
                background: u.archivado ? 'rgba(96,165,250,0.1)' : 'rgba(251,191,36,0.1)'
              }} data-id="element-886">
                        {u.archivado ? 'Desarchivar' : 'Archivar'}
                      </button>}
                  </div>
                </div>;
        })}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="usuarios" data-id="element-887" />
        </> : <div style={{
      ...glassLight,
      overflow: 'hidden'
    }} data-id="element-888">
          <div style={{
        overflowX: 'auto'
      }} data-id="element-889">
            <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 13
        }} data-id="element-890">
              <thead data-id="element-891">
                <tr style={{
              background: T.surfaceSubtle
            }} data-id="element-892">
                  {['Usuario', 'Correo', 'Rol', 'Sucursales asignadas', 'Acciones'].map(h => <th key={h} style={{
                padding: '13px 16px',
                fontWeight: 700,
                fontSize: 10,
                letterSpacing: '.1em',
                textTransform: 'uppercase',
                color: T.textMuted,
                textAlign: 'left',
                borderBottom: `1px solid ${T.border}`
              }} data-id="element-893">
                      {h}
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-894">
                {pageItems.map((u, i) => {
              const uSucIds = Array.isArray(u.sucursalesIds) ? u.sucursalesIds : [];
              const sucNames = uSucIds.length === 0 ? <span style={{
                color: '#c4b5fd',
                fontStyle: 'italic'
              }} data-id="element-895">
                        Todas las sucursales
                      </span> : uSucIds.map(sid => {
                const s = state.sucs.find(x => x.id === sid);
                return s ? s.nombre : sid;
              }).join(', ');
              const rc = rolColor[u.rol] ?? {
                color: T.textMuted,
                soft: 'rgba(255,255,255,0.06)'
              };
              return <tr key={u.id} className="getr-row" style={{
                background: i % 2 === 1 ? T.surface : 'transparent',
                borderBottom: i < pageItems.length - 1 ? `1px solid ${T.border}` : 'none',
                transition: 'background .15s'
              }} data-id="element-896">
                      <td style={{
                  padding: '13px 16px',
                  fontWeight: 600,
                  color: T.text
                }} data-id="element-897">
                        {u.nombre}
                      </td>
                      <td style={{
                  padding: '13px 16px',
                  color: T.textSecondary
                }} data-id="element-898">
                        {u.correo}
                      </td>
                      <td style={{
                  padding: '13px 16px'
                }} data-id="element-899">
                        <Badge label={rolLabel[u.rol]} color={rc.color} soft={rc.soft} data-id="element-900" />
                      </td>
                      <td style={{
                  padding: '13px 16px',
                  color: T.textSecondary,
                  maxWidth: 260
                }} data-id="element-901">
                        {sucNames}
                      </td>
                      <td style={{
                  padding: '13px 16px'
                }} data-id="element-902">
                        <div style={{
                    display: 'flex',
                    gap: 6
                  }} data-id="element-903">
                          <button className="getr-btn-ghost" onClick={() => onEdit(u.id)} style={ghostButton} data-id="element-904">
                            Editar
                          </button>
                          {u.rol !== 'admin' && <button onClick={() => setConfirm(u.id)} style={{
                      ...ghostButton,
                      color: u.archivado ? T.info : T.warn,
                      borderColor: u.archivado ? 'rgba(96,165,250,0.3)' : 'rgba(251,191,36,0.3)',
                      background: u.archivado ? 'rgba(96,165,250,0.1)' : 'rgba(251,191,36,0.1)'
                    }} data-id="element-905">
                              {u.archivado ? 'Desarchivar' : 'Archivar'}
                            </button>}
                        </div>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>
          <div style={{
        padding: '4px 16px 14px'
      }} data-id="element-906">
            <Pagination page={page} totalPages={totalPages} onChange={setPage} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="usuarios" data-id="element-907" />
          </div>
        </div>}

      {confirm && <Modal title="Confirmar acción" onClose={() => setConfirm(null)} data-id="element-908">
          <div style={{
        color: T.textSecondary,
        marginBottom: 24,
        fontSize: 14,
        lineHeight: 1.5
      }} data-id="element-909">
            ¿Estás seguro de que deseas{' '}
            {state.usuarios.find(u => u.id === confirm)?.archivado ? 'desarchivar' : 'archivar'}{' '}
            a este usuario?
          </div>
          <div style={{
        display: 'flex',
        gap: 12,
        justifyContent: 'flex-end'
      }} data-id="element-910">
            <button onClick={() => setConfirm(null)} style={{
          ...ghostButton,
          padding: '10px 20px'
        }} data-id="element-911">
              Cancelar
            </button>
            <button onClick={() => {
          onDelete(confirm);
          setConfirm(null);
        }} style={{
          ...primaryButton,
          background: state.usuarios.find(u => u.id === confirm)?.archivado ? T.info : T.warn,
          borderColor: state.usuarios.find(u => u.id === confirm)?.archivado ? T.info : T.warn,
          color: '#0B1120'
        }} data-id="element-912">
              Sí,{' '}
              {state.usuarios.find(u => u.id === confirm)?.archivado ? 'desarchivar' : 'archivar'}
            </button>
          </div>
        </Modal>}
    </PageShell>;
}